import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * Modal - Shared modal component with proper positioning
 * Uses React Portal to render at document.body level for correct fixed positioning
 * 
 * Features:
 * - Centers on screen when content fits
 * - Aligns to top with margin when content is tall
 * - Always keeps header visible
 * - Prevents body scroll when open
 * - Click outside to close
 * - ESC key to close
 * 
 * Usage:
 * <Modal onClose={handleClose} maxWidth="max-w-lg">
 *   <Modal.Header onClose={handleClose}>
 *     <h3>Title</h3>
 *   </Modal.Header>
 *   <Modal.Body>
 *     Content here
 *   </Modal.Body>
 *   <Modal.Footer>
 *     <button>Close</button>
 *   </Modal.Footer>
 * </Modal>
 */

const Modal = ({ 
  children, 
  onClose, 
  maxWidth = 'max-w-lg',
  className = '',
  preventClose = false 
}) => {
  const modalRef = useRef(null);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && !preventClose) {
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose, preventClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Focus trap - focus modal on mount
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !preventClose) {
      onClose?.();
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
      onClick={handleBackdropClick}
      data-testid="modal-backdrop"
    >
      {/* Flexbox container with min-height to enable centering for small content */}
      <div className="min-h-[100dvh] flex items-start sm:items-center justify-center p-4">
        <div 
          ref={modalRef}
          tabIndex={-1}
          className={`
            glass-card ${maxWidth} w-full 
            my-4 sm:my-8
            max-h-[90dvh]
            overflow-y-auto flex flex-col
            ${className}
          `}
          onClick={(e) => e.stopPropagation()}
          data-testid="modal-content"
        >
          {children}
        </div>
      </div>
    </div>
  );

  // Use React Portal to render at document.body level
  return ReactDOM.createPortal(modalContent, document.body);
};

// Header sub-component
Modal.Header = ({ children, onClose, className = '' }) => (
  <div className={`
    flex-shrink-0
    bg-gradient-to-r from-blue-900/90 to-blue-800/90
    px-6 py-4 
    border-b border-blue-400/20 
    flex justify-between items-center
    ${className}
  `}>
    <div className="flex-1">{children}</div>
    {onClose && (
      <button 
        onClick={onClose} 
        className="text-red-400 hover:text-red-300 text-xl font-bold ml-4 flex-shrink-0"
        aria-label="Close modal"
        data-testid="modal-close-btn"
      >
        ×
      </button>
    )}
  </div>
);

// Body sub-component with scroll
Modal.Body = ({ children, className = '' }) => (
  <div className={`flex-1 overflow-y-auto p-6 ${className}`}>
    {children}
  </div>
);

// Footer sub-component
Modal.Footer = ({ children, className = '' }) => (
  <div className={`
    flex-shrink-0
    px-6 py-4 
    border-t border-blue-400/20 
    bg-blue-900/30
    ${className}
  `}>
    {children}
  </div>
);

export default Modal;
